import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import {
  EventqDialogPopover,
  EventqDialogPopoverPlacement,
} from '../dialog-popover/dialog-popover';
import {
  EventqSearchableList,
  type EventqSearchableListColumn,
  type EventqSearchableListRow,
} from '../searchable-list/searchable-list';
import type { EventqSiteLocation, EventqSiteSelectionConfirm } from './site-selection-popover.types';

export type { EventqSiteLocation, EventqSiteSelectionConfirm };

const SITE_COLUMNS: EventqSearchableListColumn[] = [
  { key: 'name', style: 'primary' },
  { key: 'address', style: 'secondary' },
];

@Component({
  selector: 'eventq-site-selection-popover',
  imports: [EventqDialogPopover, EventqSearchableList],
  templateUrl: './site-selection-popover.html',
  styleUrl: './site-selection-popover.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventqSiteSelectionPopover {
  readonly open = input(false);
  readonly title = input.required<string>();
  readonly searchPlaceholder = input('Search');
  readonly cancelLabel = input('Cancel');
  readonly confirmLabel = input.required<string>();
  readonly sites = input.required<EventqSiteLocation[]>();
  readonly selectedSiteId = input<string | null>(null);
  /** When true, forwards to the inner searchable list skeleton. */
  readonly loading = input(false);
  /** Skeleton row count while loading. Defaults to the searchable-list default. */
  readonly skeletonCount = input<number | null>(null);
  readonly searchQuery = input('');
  readonly anchor = input<HTMLElement | null>(null);
  readonly placement = input<EventqDialogPopoverPlacement | 'auto'>('auto');
  readonly panelWidth = input<string | null>(null);
  /** Caps panel height; panel still shrinks with few sites. */
  readonly panelMaxHeight = input<string | null>('560px');

  readonly closed = output<void>();
  readonly confirm = output<EventqSiteSelectionConfirm>();
  readonly searchQueryChange = output<string>();
  readonly selectedSiteIdChange = output<string>();

  readonly siteColumns = SITE_COLUMNS;

  readonly siteRows = computed<EventqSearchableListRow[]>(() =>
    this.sites().map((site) => {
      const armed = site.armed !== false;
      return {
        id: site.id,
        name: site.name,
        address: site.address,
        leadingIcon: armed ? 'verified_user' : 'security',
        leadingIconTone: armed ? 'armed' : 'disarmed',
      };
    }),
  );

  readonly selectedIds = computed(() => {
    const id = this.selectedSiteId();
    return id ? [id] : [];
  });

  readonly resolvedPlacement = computed((): EventqDialogPopoverPlacement => {
    const placement = this.placement();
    if (placement !== 'auto') {
      return placement;
    }

    return this.anchor() ? 'anchor-top-end' : 'center';
  });

  onSiteSelect(siteId: string): void {
    this.selectedSiteIdChange.emit(siteId);
  }

  onConfirm(): void {
    const siteId = this.selectedSiteId();
    if (!siteId) {
      return;
    }

    this.confirm.emit({ siteId });
  }
}
